// JSON Schema for Annual Review Form
export const AnnualReviewFormSchema = {
  formId: "annual_review_2026",
  formName: "Annual Performance Review",
  formType: "annual",
  version: "1.0",
  sections: [
    {
      sectionId: "employee_details",
      sectionTitle: "Employee Details",
      description: "This section to be completed by the employee",
      fields: [
        {
          fieldId: "employee_name",
          fieldLabel: "Employee Name",
          fieldType: "text",
          required: true,
          readOnly: false
        },
        {
          fieldId: "job_title",
          fieldLabel: "Job Title",
          fieldType: "text",
          required: true,
          readOnly: false
        },
        {
          fieldId: "manager",
          fieldLabel: "Manager",
          fieldType: "text",
          required: true,
          readOnly: false
        },
        {
          fieldId: "date",
          fieldLabel: "Date",
          fieldType: "date",
          required: true,
          readOnly: false
        }
      ]
    },
    {
      sectionId: "self_review",
      sectionTitle: "Self Review",
      description: "To be completed by the employee",
      completedBy: "employee",
      fields: [
        {
          fieldId: "accomplishments",
          fieldLabel: "1. What were your key accomplishments during the last year?",
          fieldType: "textarea",
          required: true,
          rows: 4
        },
        {
          fieldId: "development_areas",
          fieldLabel: "2. Where do you believe you could use development to either be successful in your current role or to prepare you for future roles?",
          fieldType: "textarea",
          required: true,
          rows: 4
        },
        {
          fieldId: "attributes_heading",
          fieldLabel: "3. How would you rate yourself on these attributes?",
          fieldType: "heading",
          required: false
        },
        {
          fieldId: "dependability",
          fieldLabel: "Dependability:\nReliable, steady, and trustworthy. People can count on you to get work done on time and with good quality.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "attributes_heading"
        },
        {
          fieldId: "productivity",
          fieldLabel: "Productivity:\nYou perform at the highest level of your abilities and use your skills and resources efficiently to achieve the best possible outcome.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "attributes_heading"
        },
        {
          fieldId: "teamwork",
          fieldLabel: "Teamwork:\nCollaborative and communicative. Others can count on you to work together to successfully complete tasks and help create a positive work environment.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "attributes_heading"
        },
        {
          fieldId: "organizational_commitment",
          fieldLabel: "4. Organizational Commitment & Adherence - How reliably did you follow and adhere to organizational processes, security practices, and standards?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "comments",
          fieldLabel: "5. Additional Comments",
          fieldType: "textarea",
          required: true,
          rows: 4
        }
      ]
    },
    {
      sectionId: "manager_review",
      sectionTitle: "Manager Comments",
      description: "To be completed by the manager after reviewing the employee's self-evaluation",
      completedBy: "manager",
      fields: [
        {
          fieldId: "manager_accomplishments",
          fieldLabel: "1. What would you describe as the individual's key accomplishments during the last year?",
          fieldType: "textarea",
          required: true,
          rows: 4
        },
        {
          fieldId: "manager_development_areas",
          fieldLabel: "2. What areas of development have you identified for the individual?",
          fieldType: "textarea",
          required: true,
          rows: 4
        },
        {
          fieldId: "manager_attributes_heading",
          fieldLabel: "3. How would you rate the individual on these attributes?",
          fieldType: "heading",
          required: false
        },
        {
          fieldId: "manager_dependability",
          fieldLabel: "Dependability:\nReliable, steady, and trustworthy. Others can count on the individual to complete work on time and with good quality.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "manager_attributes_heading"
        },
        {
          fieldId: "manager_productivity",
          fieldLabel: "Productivity:\nThe individual performs at the highest level of their abilities and uses their skills and resources efficiently to achieve the best possible outcome.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "manager_attributes_heading"
        },
        {
          fieldId: "manager_teamwork",
          fieldLabel: "Teamwork:\nCollaborative and communicative. Others can count on the individual to work effectively with team members to complete tasks and maintain a positive work environment.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "manager_attributes_heading"
        },
        {
          fieldId: "manager_organizational_commitment",
          fieldLabel: "4. Organizational Commitment & Adherence - How reliably did the individual follow and adhere to organizational processes, security practices, and standards?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "overall_performance",
          fieldLabel: "5. Overall Performance - Taking into consideration the individual's experience level and role, how would you rate their overall performance during this review period?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "summary",
          fieldLabel: "6. Summary - Please provide any additional feedback, development recommendations, or comments regarding the individual's performance.",
          fieldType: "textarea",
          required: true,
          rows: 4
        }
      ]
    }
  ]
};

// JSON Schema for Probation Review Form
export const ProbationReviewFormSchema = {
  formId: "probation_review_2026",
  formName: "Probation Performance Review",
  formType: "probation",
  version: "1.0",
  sections: [
    {
      sectionId: "employee_details",
      sectionTitle: "Employee Details",
      description: "This section to be completed by the employee",
      fields: [
        {
          fieldId: "employee_name",
          fieldLabel: "Employee Name",
          fieldType: "text",
          required: true,
          readOnly: false
        },
        {
          fieldId: "job_title",
          fieldLabel: "Job Title",
          fieldType: "text",
          required: true,
          readOnly: false
        },
        {
          fieldId: "first_level_manager",
          fieldLabel: "First Level Manager",
          fieldType: "text",
          required: true,
          readOnly: false
        },
        {
          fieldId: "second_level_manager",
          fieldLabel: "Second Level Manager",
          fieldType: "text",
          required: false,
          readOnly: false
        }
      ]
    },
    {
      sectionId: "self_evaluation",
      sectionTitle: "Employee Self-Evaluation",
      description: "To be completed by the employee and shared with the manager prior to the review discussion",
      completedBy: "employee",
      fields: [
        {
          fieldId: "test_case_design",
          fieldLabel: "1. How effectively did you design clear, structured, and reusable test cases based on requirements, user stories, and acceptance criteria?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "manual_testing",
          fieldLabel: "2. How effectively did you perform manual testing and identify defects with clear reproduction steps and supporting evidence?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "product_knowledge",
          fieldLabel: "3. How effectively did you improve your understanding of the product, testing tools, QA processes, and the mortgage domain during this period?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "attributes_heading",
          fieldLabel: "4. How would you rate yourself on these attributes?",
          fieldType: "heading",
          required: false
        },
        {
          fieldId: "dependability",
          fieldLabel: "Dependability:\nReliable, steady, and trustworthy. People can count on you to get work done on time and with good quality.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "attributes_heading"
        },
        {
          fieldId: "productivity",
          fieldLabel: "Productivity:\nYou perform at the highest level of your abilities and use your skills and resources efficiently to achieve the best possible outcome.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "attributes_heading"
        },
        {
          fieldId: "teamwork",
          fieldLabel: "Teamwork:\nCollaborative and communicative. Others can count on you to work together to successfully complete tasks and help create a positive work environment.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "attributes_heading"
        },
        {
          fieldId: "improvements",
          fieldLabel: "5. Based on your experience during this period, what improvements would you recommend to help the team or organization work more effectively?",
          fieldType: "textarea",
          required: true,
          rows: 4
        }
      ]
    },
    {
      sectionId: "manager_evaluation",
      sectionTitle: "Manager Evaluation",
      description: "To be completed by the manager after reviewing the employee's self-evaluation",
      completedBy: "manager",
      fields: [
        {
          fieldId: "manager_test_case_design",
          fieldLabel: "1. How effectively did the individual design clear, structured, and reusable test cases based on requirements, user stories, and acceptance criteria?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "manager_manual_testing",
          fieldLabel: "2. How effectively did the individual perform manual testing and identify defects with clear reproduction steps and supporting evidence?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "manager_product_knowledge",
          fieldLabel: "3. How effectively did the individual improve their understanding of the product, testing tools, QA processes, and the mortgage domain during this period?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "manager_attributes_heading",
          fieldLabel: "4. How would you rate the individual on these attributes?",
          fieldType: "heading",
          required: false
        },
        {
          fieldId: "manager_dependability",
          fieldLabel: "Dependability:\nReliable, steady, and trustworthy. Others can count on the individual to complete work on time and with good quality.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "manager_attributes_heading"
        },
        {
          fieldId: "manager_productivity",
          fieldLabel: "Productivity:\nThe individual performs at the highest level of their abilities and uses their skills and resources efficiently to achieve the best possible outcome.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "manager_attributes_heading"
        },
        {
          fieldId: "manager_teamwork",
          fieldLabel: "Teamwork:\nCollaborative and communicative. Others can count on the individual to work effectively with team members to complete tasks and maintain a positive work environment.",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"],
          groupUnder: "manager_attributes_heading"
        },
        {
          fieldId: "overall_performance",
          fieldLabel: "5. Overall Performance - Taking into consideration the individual's experience level and role, how would you rate their overall performance during this review period?",
          fieldType: "radio",
          required: true,
          options: ["Needs Improvement", "Meets Expectations", "Exceeds Expectations"]
        },
        {
          fieldId: "probation_progress",
          fieldLabel: "6. Probation Progress Assessment - Based on the individual's performance during this review period, how would you assess their progress?",
          fieldType: "radio",
          required: true,
          options: [
            "On track to successfully complete probation",
            "Needs improvement during the remaining probation period",
            "Requires additional review"
          ]
        },
        {
          fieldId: "manager_comments",
          fieldLabel: "7. Additional Comments and Recommendations - Please provide any additional feedback, development recommendations, or comments regarding the individual's performance and probation progress.",
          fieldType: "textarea",
          required: false,
          rows: 4
        }
      ]
    }
  ]
};


// Export both schemas
export const FormSchemas = {
  annual: AnnualReviewFormSchema,
  probation: ProbationReviewFormSchema
};
